import React, { useState } from 'react';
import { PlaybookAdherence, getScoreBadgeClasses } from './types';
import { ProgressBar, scoreTone } from '../ui/ProgressBar';

interface PlaybookAdherenceSectionProps {
  playbook: PlaybookAdherence;
}

interface CoverageSection {
  label: string;
  total: number;
  delivered: number;
  pct: number;
  missedItems: string[];
  qualitySummary: string;
}

const MissedItemsList: React.FC<{ items: string[]; label: string }> = ({ items, label }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
        type="button"
      >
        {isExpanded ? `Hide ${label}` : `Show ${label} (${items.length})`}
        <span className="ml-1">{isExpanded ? '▲' : '▼'}</span>
      </button>
      {isExpanded && (
        <ul className="mt-2 space-y-1">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="text-sm text-ink bg-error-tint border border-error rounded px-2 py-1"
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CoverageBar: React.FC<{ section: CoverageSection }> = ({ section }) => {
  const pct = Math.min(100, Math.max(0, section.pct));
  const hasItems = section.total > 0;

  return (
    <div className="bg-white border border-hairline rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-ink-secondary">{section.label}</span>
        {hasItems && (
          <span className="text-sm text-ink-secondary">
            {section.delivered}/{section.total}{' '}
            <span className="font-bold">{Math.round(pct)}%</span>
          </span>
        )}
      </div>
      {hasItems && (
        <ProgressBar value={pct} tone={scoreTone(pct)} />
      )}
      {section.qualitySummary && (
        <p className="text-xs text-ink-muted mt-2 italic">{section.qualitySummary}</p>
      )}
      <MissedItemsList items={section.missedItems} label="Missed Items" />
    </div>
  );
};

const PlaybookAdherenceSection: React.FC<PlaybookAdherenceSectionProps> = ({ playbook }) => {
  if (!playbook?.was_used) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-4xl mb-3 text-ink-disabled font-light">[--]</div>
        <p className="text-lg font-semibold text-ink-secondary">No Sales Playbook Used</p>
        <p className="text-sm text-ink-muted mt-1 max-w-sm">
          The sales rep did not follow a structured sales playbook during this session.
          Consider reviewing playbook adoption with the rep.
        </p>
      </div>
    );
  }

  const coverageSections: CoverageSection[] = [
    {
      label: 'Intro Coverage',
      total: playbook.intro_coverage?.total_available ?? 0,
      delivered: playbook.intro_coverage?.total_delivered ?? 0,
      pct: playbook.intro_coverage?.coverage_pct ?? 0,
      missedItems: playbook.intro_coverage?.missed_intro_points ?? [],
      qualitySummary: playbook.intro_coverage?.intro_quality_summary ?? '',
    },
    {
      label: 'Question Coverage',
      total: playbook.question_coverage?.total_available ?? 0,
      delivered: playbook.question_coverage?.total_asked ?? 0,
      pct: playbook.question_coverage?.coverage_pct ?? 0,
      missedItems: playbook.question_coverage?.missed_questions ?? [],
      qualitySummary: playbook.question_coverage?.question_quality_summary ?? '',
    },
    {
      label: 'Deal Progression Coverage',
      total: playbook.deal_progression_coverage?.total_available ?? 0,
      delivered: playbook.deal_progression_coverage?.total_delivered ?? 0,
      pct: playbook.deal_progression_coverage?.coverage_pct ?? 0,
      missedItems: playbook.deal_progression_coverage?.missed_progression_points ?? [],
      qualitySummary: playbook.deal_progression_coverage?.progression_summary ?? '',
    },
  ];

  return (
    <div className="space-y-5">
      {/* Overall score + summary */}
      <div className="bg-white border border-hairline rounded-lg p-4 flex flex-wrap items-start gap-4">
        <div className="flex-shrink-0">
          <span
            className={`text-2xl font-bold px-4 py-2 rounded ${getScoreBadgeClasses(
              playbook.overall_score
            )}`}
          >
            {playbook.overall_score}/100
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-secondary mb-1">Overall Playbook Score</p>
          {playbook.overall_summary && (
            <p className="text-sm text-ink-secondary">{playbook.overall_summary}</p>
          )}
        </div>
      </div>

      {/* Coverage sections */}
      <div className="space-y-3">
        {coverageSections.map((section) => (
          <CoverageBar key={section.label} section={section} />
        ))}
      </div>

      {/* Recommendations */}
      {playbook.recommendations && playbook.recommendations.length > 0 && (
        <div className="bg-info-tint border border-info rounded-lg p-4">
          <p className="text-sm font-semibold text-info mb-2">Coaching Recommendations</p>
          <ol className="space-y-1 list-decimal list-inside">
            {playbook.recommendations.map((rec, idx) => (
              <li key={idx} className="text-sm text-ink">
                {rec}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default PlaybookAdherenceSection;
